import { useState } from "react"
import { ClientValidationException } from "@/types/client-exceptions"
import {
  IMAGE_FILE_EXTENSIONS,
  MAX_AVATAR_IMAGE_SIZE,
  VALID_IMAGE_TYPES,
} from "@/types/media"
import {
  CurrentUser,
  CurrentUserProfile,
  UserProfileUpdates,
} from "@/types/user"
import isEqual from "lodash/isEqual"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { refreshAuthToken } from "@/lib/auth/client"
import { buildClientAxios } from "@/lib/client-axios"
import {
  initialSettingProfileFormState,
  SettingProfileState,
  SettingsProfileFormValue,
  UserProfileServerValidations,
  validateSettingsProfileFormValue,
} from "@/lib/settings-form-utils"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import userSlice, {
  selectUser,
  selectUserProfile,
} from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { UserApi } from "@/lib/user-api"
import { empty } from "@/lib/utils/presence"
import { DirectFileUpload } from "@/components/direct-file-upload"
import { SettingsProfileForm } from "@/components/settings-profile-form"
import { fieldValueChanged } from "@/components/settings-util"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Form } from "@/components/ui/form"
import { Loading } from "@/components/ui/loading"
import { UserAvatar } from "@/components/user-avatar"
import { UserHandle } from "@/components/user-handle"

interface EditProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Called after a save with the user the server returned. The dialog stays
   * open; the caller closes it, and can navigate if the handle changed.
   */
  onSaved: (user: CurrentUser) => void
}

export function EditProfileDialog({
  open,
  onOpenChange,
  onSaved,
}: EditProfileDialogProps) {
  const user = useAppSelector(selectUser)
  const userProfile = useAppSelector(selectUserProfile)

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) sendAnalyticsEvent("ViewEditProfilePage")
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent size="lg">
        <EditProfileForm
          user={user}
          userProfile={userProfile}
          onSaved={onSaved}
        />
      </DialogContent>
    </Dialog>
  )
}

interface EditProfileFormProps {
  user: CurrentUser
  userProfile: CurrentUserProfile
  onSaved: (user: CurrentUser) => void
}

export function EditProfileForm({
  user,
  userProfile,
  onSaved,
}: EditProfileFormProps) {
  const dispatch = useAppDispatch()

  const userApi = new UserApi(buildClientAxios())

  const [loading, setLoading] = useState(false)
  const [profileUpdated, setProfileUpdated] = useState<boolean>(false)

  const [formState, setFormState] = useState<SettingProfileState>(() =>
    initialSettingProfileFormState(user, userProfile)
  )
  const [showValidation, setShowValidation] = useState<boolean>(
    !empty(Object.values(formState[0]).filter((v) => !empty(v)))
  )

  const settingsProfileUpdated = (
    _prev: SettingsProfileFormValue,
    updated: SettingsProfileFormValue
  ) => {
    setFormState((prevValue) => {
      const [_, validated, errors] = validateSettingsProfileFormValue(updated)
      const updatedSettingProfile: SettingProfileState = [
        { ...prevValue[0], ...updated },
        validated,
        errors,
      ]
      // Did handle/username changed?  If so, we want to check if this
      // handle/username is already used by someone else.
      if (updated.handle && prevValue[0].handle !== updated.handle) {
        void (async () => {
          const handleInfo = await userApi.checkHandle(updated.handle)
          // Yes, it is used by someone else, we want to add the error message in.
          if (!handleInfo.available) {
            const newErrors = {
              ...{ _errors: [], handle: { _errors: [] } },
              ...errors,
            }
            newErrors.handle?._errors.push(
              handleInfo.reserved
                ? "Contact us to claim your business"
                : `${updated.handle} is already in use by another user. Pick a new username.`
            )
            const newHandleSettingProfile: SettingProfileState = [
              updated,
              null,
              newErrors,
            ]
            setFormState(newHandleSettingProfile)
          }
        })()
      }

      setProfileUpdated(
        !isEqual(prevValue[0], {
          ...prevValue[0],
          ...updated,
        })
      )

      return updatedSettingProfile
    })
  }

  const save = async () => {
    // With first save/submit, we want to start showing errors
    setShowValidation(true)

    let userProfileDTO: UserProfileUpdates = {}
    // formState[1] is all the validated fields.  If it is not empty
    // it means that the form values are valid.  So process the form submit
    if (formState[1] != null) {
      // Don't want to write out values that are not changed
      const newFirstName = formState[1]?.first_name || ""
      if (fieldValueChanged(user.first_name, newFirstName)) {
        userProfileDTO = {
          ...userProfileDTO,
          first_name: newFirstName,
        }
      }
      const newLastName = formState[1]?.last_name || ""
      if (fieldValueChanged(user.last_name, newLastName)) {
        userProfileDTO = {
          ...userProfileDTO,
          last_name: newLastName,
        }
      }
      const newHandle = formState[1]?.handle
      if (fieldValueChanged(user.handle, newHandle)) {
        userProfileDTO = { ...userProfileDTO, handle: newHandle }
      }
      const newWebsite = formState[1]?.website
      if (
        newWebsite !== undefined &&
        fieldValueChanged(userProfile.website, newWebsite)
      ) {
        userProfileDTO = { ...userProfileDTO, website: newWebsite }
      }
      const newLocation = formState[1]?.location
      if (
        newLocation !== undefined &&
        fieldValueChanged(userProfile.location, newLocation)
      ) {
        userProfileDTO = { ...userProfileDTO, location: newLocation }
      }
      const newBio = formState[1]?.bio
      if (newBio != null && fieldValueChanged(userProfile.bio, newBio)) {
        userProfileDTO = { ...userProfileDTO, bio: newBio }
      }
      const newShopLink = formState[1]?.shop_link
      if (
        newShopLink != null &&
        fieldValueChanged(userProfile.shop_link, newShopLink)
      ) {
        userProfileDTO = { ...userProfileDTO, shop_link: newShopLink }
      }
      const newInstagram = formState[1]?.instagram
      if (
        newInstagram != null &&
        fieldValueChanged(userProfile.instagram, newInstagram)
      ) {
        userProfileDTO = { ...userProfileDTO, instagram: newInstagram }
      }
      const newTiktok = formState[1]?.tiktok
      if (
        newTiktok != null &&
        fieldValueChanged(userProfile.tiktok, newTiktok)
      ) {
        userProfileDTO = { ...userProfileDTO, tiktok: newTiktok }
      }
      const newYoutube = formState[1]?.youtube
      if (
        newYoutube != null &&
        fieldValueChanged(userProfile.youtube, newYoutube)
      ) {
        userProfileDTO = { ...userProfileDTO, youtube: newYoutube }
      }
      if (!empty(userProfileDTO)) {
        try {
          const response = await userApi.updateUserProfile(userProfileDTO)
          if (response.user) {
            setProfileUpdated(false)
            dispatch(userSlice.actions.setUser(response.user))
            dispatch(userSlice.actions.setUserProfile(response.user_profile))
          }
          try {
            // A profile save may change fields in the session's CurrentUser
            // snapshot now or in the future. Keep that snapshot current.
            await refreshAuthToken()
          } catch {
            // Redux is already updated; the token will get it on next refresh.
          }
          toastManager.add({ type: "success", description: "Profile saved." })
          sendAnalyticsEvent("UserProfileUpdate")
          if (response.user) onSaved(response.user)
        } catch (e) {
          if (e instanceof ClientValidationException) {
            const clientValidationException =
              e as ClientValidationException<UserProfileServerValidations>
            const error = clientValidationException.data
            const updatedSettingProfile: SettingProfileState = [
              formState[0],
              null,
              error,
            ]
            setFormState(updatedSettingProfile)
          } else {
            throw e
          }
        }
      }
    }
  }

  const handleValidate = (file: File) => {
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      return `Image file must be ${IMAGE_FILE_EXTENSIONS.join(", ")}`
    }
    if (file.size > MAX_AVATAR_IMAGE_SIZE) {
      return `Image file must be ${MAX_AVATAR_IMAGE_SIZE / 1024 / 1024}MB or less`
    }
    return undefined
  }

  const handleFormSubmit = async (file: File) => {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", "avatar")

    const result = await userApi.uploadAvatarImage(formData)
    if (result.url) {
      dispatch(userSlice.actions.patchUser({ image_url: result.url }))
      try {
        await refreshAuthToken()
      } catch {
        // redux is already updated; cookie refresh is what survives reload
      }
    }
    return { error: result.error, url: result.url }
  }

  return (
    <Form
      className="contents"
      onSubmit={async (e) => {
        e.preventDefault()
        setLoading(true)
        await save()
        setLoading(false)
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit profile</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <DirectFileUpload
          validateCallback={handleValidate}
          formSubmitCallback={handleFormSubmit}
        >
          {({ uploading, openFilePicker }) => (
            <button
              type="button"
              disabled={uploading}
              onClick={openFilePicker}
              className="group mb-5 flex items-center gap-2.5"
            >
              <span className="relative shrink-0">
                <UserAvatar size="lg" />
                {uploading && (
                  <span className="absolute inset-0 flex animate-in items-center justify-center overflow-hidden rounded-full bg-background/50 backdrop-blur-md fade-in">
                    <Loading />
                  </span>
                )}
              </span>
              <span>
                <UserHandle className="font-semibold" showVerified />
                <div className="mt-0.5 text-sm text-muted group-hover:text-foreground">
                  {uploading ? "Updating…" : "Change profile photo"}
                </div>
              </span>
            </button>
          )}
        </DirectFileUpload>
        <div className="flex flex-col gap-4">
          <SettingsProfileForm
            formValue={formState[0]}
            showValidation={showValidation}
            errors={formState[2]}
            onValueChange={settingsProfileUpdated}
          />
        </div>
      </DialogBody>
      <DialogFooter>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={!profileUpdated}
          loading={loading}
        >
          Save
        </Button>
      </DialogFooter>
    </Form>
  )
}
